import { Stack } from "@mui/material";
import { LicenseStatuses } from "@src/appV2/Licenses/api/types";
import { useGetLicenses } from "@src/appV2/Licenses/api/useGetLicenses";
import { PageFooterWrapper } from "@src/appV2/redesign/components/PageFooterWrapper";
import { useGetWorker } from "@src/appV2/Worker/api/useGetWorker";
import { useRef } from "react";

import { LoadingButton } from "../../../components/LoadingButton";
import { PageWrapper } from "../../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../../components/StickyScrollPageHeader";
import { DocumentDetailsSection } from "../../Details/DetailsSection";
import { DocumentPageScrollWrapper } from "../../Details/ScrollWrapper";
import { IndependentContractorBadgeShowButton } from "./ShowButton";

export function IndependentContractorBadgePageV1() {
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: worker, isLoading: isWorkerLoading } = useGetWorker();
  const { data: allWorkerLicenses, isLoading: isLicensesLoading } = useGetLicenses({
    workerId: worker?.userId ?? "",
  });
  const isLoading = isWorkerLoading || isLicensesLoading;

  const activeWorkerLicenses =
    allWorkerLicenses?.filter((license) => license.status === LicenseStatuses.ACTIVE) ?? [];
  const hasNoLicenses = activeWorkerLicenses.length === 0;
  const hasOneLicense = activeWorkerLicenses.length === 1;
  const hasMultipleLicenses = activeWorkerLicenses.length > 1;

  return (
    <PageWrapper variant="tertiary">
      <StickyScrollPageHeader
        shouldWrapTitle
        title="Independent Contractor Badge"
        headerVariant="tertiary"
        scrollContainerRef={scrollRef}
      />

      <DocumentPageScrollWrapper ref={scrollRef}>
        <DocumentDetailsSection
          title=""
          description={`
Some workplaces may require you to have an independent contractor badge to
access their facilities.

Make sure to update your profile picture to a recent photo of yourself before
generating your badge. Many workplaces require an accurate and recent photo.

${
  hasMultipleLicenses ? "You should select the proper license from the buttons below" : ""
}. This will generate a badge that you can print and use in the workplace.
`}
        />

        {isLoading && (
          <Stack spacing={4} sx={{ alignItems: "center" }}>
            <LoadingButton fullWidth disabled isLoading size="large" variant="outlined">
              Loading...
            </LoadingButton>
          </Stack>
        )}

        {!isLoading && hasNoLicenses && (
          <Stack spacing={4} sx={{ alignItems: "center" }}>
            <LoadingButton fullWidth disabled size="large" variant="outlined" isLoading={false}>
              You have no active license
            </LoadingButton>
          </Stack>
        )}

        {!isLoading &&
          hasMultipleLicenses &&
          activeWorkerLicenses?.map((license) => (
            <IndependentContractorBadgeShowButton
              key={license.id}
              licenseNumber={license.number ?? undefined}
              qualification={license.qualification}
            />
          ))}
      </DocumentPageScrollWrapper>

      {!isLoading && hasOneLicense && (
        <PageFooterWrapper>
          <IndependentContractorBadgeShowButton
            licenseNumber={activeWorkerLicenses[0].number ?? undefined}
            qualification={activeWorkerLicenses[0].qualification}
            buttonVariant="contained"
          />
        </PageFooterWrapper>
      )}
    </PageWrapper>
  );
}
